package com.anjuros.website.redesign.styles

import com.anjuros.website.redesign.theme.RdColor
import com.anjuros.website.redesign.theme.RdFont
import com.anjuros.website.redesign.theme.RdSize
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.hover
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px

// ---------- Hero: any design + your business = your website ----------

val DesignsEquationStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Grid).gap(10.px).css("min-width" to "0")
}

val DesignsEqCardStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Grid).gap(12.px).backgroundColor(RdColor.Paper)
        .border(1.px, LineStyle.Solid, RdColor.Line).borderRadius(16.px)
        .css("padding" to "16px 18px", "box-shadow" to RdSize.SHADOW)
}

val DesignsEqResultCardStyle = CssStyle.base {
    Modifier.css("border" to "2px solid rgb(46,75,110)")
}

val DesignsEqLabelStyle = CssStyle.base {
    Modifier.color(RdColor.Brand)
        .css("font" to "500 .74rem/1 ${RdFont.MONO_STACK}", "letter-spacing" to ".1em", "text-transform" to "uppercase")
}

val DesignsEqThumbsStyle = CssStyle {
    base { grid("repeat(4, 1fr)").gap(8.px) }
    cssRule(" img") {
        Modifier.display(DisplayStyle.Block).borderRadius(8.px)
            .css("width" to "100%", "height" to "auto", "aspect-ratio" to "4 / 3", "object-fit" to "cover")
    }
}

val DesignsEqChipsStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Flex).gap(8.px).css("flex-wrap" to "wrap", "list-style" to "none", "padding" to "0") }
    cssRule(" li") {
        Modifier.backgroundColor(RdColor.SkySoft).color(RdColor.Ink).border(1.px, LineStyle.Solid, RdColor.LogoBlue)
            .borderRadius(999.px).css("font-size" to ".88rem", "font-weight" to 500, "padding" to "6px 12px")
    }
}

val DesignsEqOperatorStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Grid).backgroundColor(RdColor.Brand).color(RdColor.BrandInk).borderRadius(50.px)
        .css("justify-self" to "center", "width" to "34px", "height" to "34px", "place-items" to "center", "font" to "700 1.15rem/1 ${RdFont.BODY_STACK}")
}

val DesignsEqSiteStyle = CssStyle.base {
    Modifier.border(1.px, LineStyle.Solid, RdColor.Line).borderRadius(10.px).css("overflow" to "hidden")
}

val DesignsEqSiteBarStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Flex).gap(5.px).backgroundColor(RdColor.Ground)
            .css("padding" to "8px 10px", "border-bottom" to "1px solid rgb(220,227,236)")
    }
    cssRule(" span") { Modifier.backgroundColor(RdColor.LogoBlue).borderRadius(50.px).css("width" to "8px", "height" to "8px") }
}

val DesignsEqSiteBodyStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Grid).gap(8.px)
            .css("padding" to "14px 16px", "background" to "linear-gradient(135deg, rgb(230,237,245), rgb(255,255,255))")
    }
    cssRule(" b") { Modifier.css("font" to "800 1.15rem/1.2 ${RdFont.DISPLAY_STACK}", "font-stretch" to "110%") }
}

val DesignsEqWordStyle = CssStyle.base {
    Modifier.color(RdColor.Brand)
}

/** Grey bars standing in for page text in the website sketch. */
val DesignsEqLineStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Block).backgroundColor(RdColor.Line).borderRadius(4.px).css("height" to "8px", "width" to "85%")
}

val DesignsEqLineShortStyle = CssStyle.base {
    Modifier.css("width" to "55%")
}

val DesignsEqButtonStyle = CssStyle.base {
    Modifier.backgroundColor(RdColor.Brand).color(RdColor.BrandInk).borderRadius(8.px)
        .css("justify-self" to "start", "margin-top" to "4px", "font-size" to ".78rem", "font-weight" to 600, "padding" to "7px 12px")
}

// ---------- Catalogue ----------

val DesignsFiltersStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Flex).gap(8.px).css("flex-wrap" to "wrap", "margin-bottom" to "28px")
}

val DesignsFilterStyle = CssStyle {
    base {
        Modifier.backgroundColor(RdColor.Paper).color(RdColor.Ink).border(1.px, LineStyle.Solid, RdColor.Line)
            .borderRadius(999.px).cursor(Cursor.Pointer)
            .css("font" to "500 .92rem/1 ${RdFont.BODY_STACK}", "padding" to "11px 16px", "transition" to "border-color .15s, background .15s")
    }
    hover { Modifier.css("border-color" to "rgb(46,75,110)") }
}

val DesignsFilterActiveStyle = CssStyle.base {
    Modifier.backgroundColor(RdColor.Brand).color(RdColor.BrandInk).css("border-color" to "rgb(46,75,110)")
}

val DesignsCatalogueStyle = CssStyle {
    base { grid("1fr").gap(24.px) }
    Breakpoint.MD { Modifier.css("grid-template-columns" to "repeat(2, 1fr)") }
}

val DesignsCardStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Grid).backgroundColor(RdColor.Paper).border(1.px, LineStyle.Solid, RdColor.Line)
            .borderRadius(16.px).css("grid-template-rows" to "auto 1fr", "overflow" to "hidden", "min-width" to "0", "transition" to "border-color .2s, transform .2s")
    }
    hover { Modifier.css("border-color" to "rgb(180,198,220)", "transform" to "translateY(-2px)") }
    cssRule(" figure") { Modifier.position(Position.Relative).css("margin" to "0", "aspect-ratio" to "16 / 9", "max-width" to "100%", "overflow" to "hidden") }
    cssRule(" img") {
        Modifier.display(DisplayStyle.Block).css("width" to "100%", "height" to "100%", "object-fit" to "cover", "transition" to "transform .5s ease")
    }
    cssRule(":hover img") { Modifier.css("transform" to "scale(1.04)") }
}

val DesignsCardBodyStyle = CssStyle {
    // Flex column so the "View … design" link can sit at the bottom of every card, however long the text above it.
    base { Modifier.display(DisplayStyle.Flex).gap(12.px).css("flex-direction" to "column", "padding" to "20px 22px 22px") }
    cssRule(" > p") { Modifier.color(RdColor.Muted).css("font-size" to ".97rem") }
}

val DesignsAlsoStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Flex).alignItems(AlignItems.Center).gap(6.px).css("flex-wrap" to "wrap", "font-size" to ".85rem") }
    cssRule(" span") { Modifier.backgroundColor(RdColor.SkySoft).color(RdColor.Ink).borderRadius(999.px).css("padding" to "5px 9px") }
    cssRule(" span:first-child") {
        Modifier.color(RdColor.Brand).css(
            "background" to "none", "padding" to "0", "margin-right" to "4px",
            "font" to "500 .7rem/1 ${RdFont.MONO_STACK}", "letter-spacing" to ".08em", "text-transform" to "uppercase"
        )
    }
}

val DesignsCardLinkStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Flex).alignItems(AlignItems.Center).gap(8.px).color(RdColor.Brand)
            .textDecorationLine(TextDecorationLine.None).css("align-self" to "flex-start", "font-weight" to 600, "margin-top" to "auto", "padding-top" to "2px")
    }
    hover { Modifier.textDecorationLine(TextDecorationLine.Underline).css("text-underline-offset" to "3px") }
}

val DesignsBadgeStyle = CssStyle.base {
    Modifier.position(Position.Absolute).css("left" to "12px", "top" to "12px")
}

val DesignsCountStyle = CssStyle.base {
    Modifier.color(RdColor.Muted).css("margin-top" to "20px", "font-size" to ".9rem")
}

// ---------- Guide ----------

val DesignsGuideStyle = CssStyle {
    base { grid("1fr").css("gap" to "36px 40px") }
    Breakpoint.LG { Modifier.css("grid-template-columns" to "repeat(3, 1fr)") }
    cssRule(" > div") {
        Modifier.display(DisplayStyle.Grid).gap(10.px)
            .css("align-content" to "start", "padding-top" to "18px", "border-top" to "2px solid rgb(180,198,220)")
    }
    cssRule(" p") { Modifier.color(RdColor.Muted).css("font-size" to ".98rem") }
    cssRule(" ul") { Modifier.display(DisplayStyle.Grid).gap(4.px).color(RdColor.Muted).css("padding-left" to "1.1em", "font-size" to ".96rem") }
}
